import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  generateBurokAppLinkNativeDeeplink,
  generateBurokAppPathNativeDeeplink,
} from '../src/utils/burokLinkDeeplink';
import { isKnownAppRoutePath } from '../src/utils/burokHosts';
import {
  resolveShortCodeOpenTarget,
  type ResolvedOpenTarget,
} from '../src/utils/shortLinkResolve';
import OpenInAppInterstitial from './OpenInAppInterstitial';
import { ShortLinkDomainHomePage } from './ShortLinkDomainHomePage';

/**
 * burok.shop/* (et link.burok.app si servi ici).
 * - Route d’app connue (kiosk, messages, friend/…) → burokapp://path
 * - Sinon shortcode → RPC get_public_short_link → target_url
 */
export function ShortLinkOpenPage() {
  const location = useLocation();
  const [target, setTarget] = useState<ResolvedOpenTarget | null>(null);
  const [loading, setLoading] = useState(true);

  const segments = location.pathname.split('/').filter(Boolean);
  const code = segments[0] ?? '';

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (segments.length === 0) {
        if (!cancelled) setLoading(false);
        return;
      }

      if (isKnownAppRoutePath(location.pathname) || segments.length > 1) {
        const openUrl = generateBurokAppPathNativeDeeplink(
          location.pathname,
          location.search
        );
        if (!cancelled) {
          setTarget({ openUrl, resolved: true });
          setLoading(false);
        }
        return;
      }

      const resolved = await resolveShortCodeOpenTarget(code);
      if (!cancelled) {
        setTarget(resolved);
        setLoading(false);
      }
    }

    setLoading(true);
    void run();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, code, segments.length]);

  if (segments.length === 0) {
    return <ShortLinkDomainHomePage />;
  }

  if (!code.trim()) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-slate-50">
        <p className="text-slate-600">Lien invalide.</p>
      </div>
    );
  }

  const openUrl =
    target?.openUrl || generateBurokAppLinkNativeDeeplink(code);

  const notFound =
    target && !target.resolved && target.error === 'not_found'
      ? 'Lien introuvable ou expiré. Vous pouvez quand même tenter d’ouvrir l’app.'
      : null;

  return (
    <OpenInAppInterstitial
      openUrl={openUrl}
      webRedirect={target?.webRedirect}
      loading={loading}
      errorMessage={notFound}
    />
  );
}

export { ShortLinkDomainHomePage };
